/**
 * Where the two tokens live in the browser.
 *
 * The access token is held in a module variable and never written to storage: it is the
 * one that grants access, it is short-lived (15 minutes), and keeping it out of
 * `localStorage` means a cross-site scripting bug has to be live at the moment of theft
 * rather than able to read a token left behind by a session that ended yesterday.
 *
 * The refresh token does go to `localStorage`, so a page reload does not sign the user
 * out. That is a real trade-off, not an oversight: `localStorage` is readable by any
 * script on the origin, so an XSS bug means a stolen refresh token. Rotation limits the
 * damage -- the server rejects a refresh token the moment it is used a second time, so a
 * stolen one stops working as soon as the real user refreshes.
 *
 * The proper fix is an httpOnly, SameSite=Strict cookie for the refresh token, which
 * needs a server change (cookie issuance plus CSRF protection on the refresh route).
 * Logged for the H5 security pass rather than done here, because it changes the API
 * contract and F5 is meant to be additive.
 */

const REFRESH_KEY = 'fmcg.refresh_token'

let accessToken: string | null = null

export function getAccessToken(): string | null {
  return accessToken
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY)
  } catch {
    // Safari in private mode, or storage disabled by policy. The session still works;
    // it just does not survive a reload.
    return null
  }
}

export function setTokens(tokens: { access_token: string; refresh_token: string }): void {
  accessToken = tokens.access_token
  try {
    localStorage.setItem(REFRESH_KEY, tokens.refresh_token)
  } catch {
    /* see getRefreshToken */
  }
}

export function clearTokens(): void {
  accessToken = null
  try {
    localStorage.removeItem(REFRESH_KEY)
  } catch {
    /* see getRefreshToken */
  }
}
